<template>
  <div class="max-w-7xl h-full m-auto py-4 px-4 flex flex-col space-y-4">
    <div class="flex flex-row justify-between space-x-4 py-2">
      <h2 class="text-2xl">Freight Review Report</h2>

      <div class="flex space-x-4 items-center">
        <Button
          v-on:click-handler="exportCSV()"
          class="btn btn-primary btn-small flex space-x-2 items-center"
          :disabled="isLoading || !gridHasData"
        >
          <span>Download CSV</span>
          <icon set="outline" name="document-download" class="h-5 w-5" />
        </Button>
        <div v-if="userCanReject" class="flex flex1 flex-row justify-end">
          <div class="relative inline-block text-left z-10">
            <div>
              <span class="rounded-md shadow-sm">
                <Button
                  :disabled="isLoading || !gridHasData || !hasRowsSelected"
                  v-on:click-handler="toggleActionsMenu"
                  class="btn btn-primary btn-small relative z-10"
                >
                  Actions
                  <icon
                    set="solid"
                    name="chevron-down"
                    class="-mr-1 ml-2 h-5 w-5"
                  />
                </Button>
                <button
                  v-if="showActionsMenu"
                  @click="showActionsMenu = false"
                  tabindex="-1"
                  class="fixed inset-0 w-full h-full bg-black opacity-50 cursor-default"
                ></button>
              </span>
            </div>

            <transition
              enter-active-class="ease-out duration-100"
              enter-class="opacity-0 scale-95"
              enter-to-class="opacity-100 scale-100"
              leave-active-class="ease-in duration-75"
              leave-class="opacity-100 scale-100"
              leave-to-class="opacity-0 scale-95"
            >
              <div
                v-if="showActionsMenu"
                class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg"
              >
                <div class="rounded-md bg-white shadow-xs">
                  <div
                    class="py-1"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="options-menu"
                  >
                    <button
                      class="block w-full px-4 py-2 text-sm leading-5 text-gray-700 text-left"
                      role="menuitem"
                      @click="confirmRejectFreight"
                    >
                      Reject Freight Shipping
                    </button>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <ag-grid-vue
      style="height: 100%; width: 100%"
      class="ag-theme-balham"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :paginationAutoPageSize="false"
      :paginationPageSize="paginationPageSize"
      :pagination="true"
      id="tasks-report"
      :gridOptions="gridOptions"
      @grid-ready="onGridReady"
      :context="context"
      :frameworkComponents="frameworkComponents"
      :defaultColDef="defaultColDef"
      :enableCellTextSelection="true"
      :ensureDomOrder="true"
      :rowSelection="rowSelection"
    >
    </ag-grid-vue>
  </div>
</template>
<script>
import { AgGridVue } from "ag-grid-vue";
import OrderLink from "./components/OrderLink";
import ShopifyOrderLink from "./components/ShopifyOrderLink";
import { ApiFactory } from "@/api/ApiFactory.js";
import dateFormatter from "@/mixins/dateFormatter";
import Button from "@/components/elements/Button.vue";
import IgnoreFraudScore from "./components/IgnoreFraudScore";
import moneyFormatter from "@/mixins/moneyFormatter";
import { date } from "@/components/agGrid/cellRenderers";
import { ModalBus } from "@/eventBus.js";
import FreightRejectConfirm from "./components/modals/FreightRejectConfirm.vue";
import auth from "@/api/auth";
const Reports = ApiFactory.get("reports");

export default {
  name: "FreightReview",
  components: {
    Button,
    AgGridVue,
    OrderLink, // eslint-disable-line
    ShopifyOrderLink, // eslint-disable-line
    IgnoreFraudScore, // eslint-disable-line
  },
  mixins: [dateFormatter, moneyFormatter],
  props: {},
  data() {
    return {
      columnDefs: null,
      rowData: null,
      paginationPageSize: 100,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      overlayLoadingTemplate: true,
      isLoading: false,
      rowSelection: "multiple",
      showActionsMenu: false,
      selectedRows: {},
    };
  },
  computed: {
    gridHasData() {
      return this.rowData && this.rowData.length;
    },
    hasRowsSelected() {
      return this.selectedRows.length;
    },
    userCanReject() {
      return auth.isUserAuthorized(["admin"]);
    },
  },
  beforeMount() {
    this.gridOptions = {
      headerHeight: 60,
      localeText: { noRowsToShow: "No Records Found" },
      onSelectionChanged: this.itemSelectionChanged,
      isRowSelectable: function (rowNode) {
        return !(
          (Number(rowNode.data.FraudScore) >= 0.1 &&
            !Number(rowNode.data.LocalSortInteger2)) ||
          rowNode.data.Status === "Restricted" ||
          (rowNode.data.ReviewReason.includes(
            "SHIP TO address verification failed!"
          ) &&
            (rowNode.data.BadAddressStatus === null ||
              rowNode.data.BadAddressStatus.toLowerCase() !== "complete"))
        );
      },
    };

    this.context = { componentParent: this };

    this.defaultColDef = {
      sortable: true,
      filter: true,
      flex: 1,
      suppressMovable: true,
      resizable: true,
    };

    this.columnDefs = [
      {
        headerName: "Order Number",
        field: "SourceOrderNumber",
        cellRendererFramework: "OrderLink",
        cellRendererParams: {
          target: "_blank",
        },
        checkboxSelection: true,
        headerCheckboxSelection: true,
      },
      {
        headerName: "Fraud Score",
        field: "FraudScore",
        valueGetter: (params) => {
          let fraudScore = Number(params.data.FraudScore).toFixed(2);
          if (fraudScore == 1) {
            return "High";
          } else if (fraudScore >= 0.1) {
            return "Medium";
          }
          return "Low";
        },
      },
      {
        headerName: "Order Date",
        field: "OrderDate",
        valueGetter: (params) =>
          new Date(params.data.OrderDate).setHours(0, 0, 0, 0),
        cellRenderer: "dateCell",
        filter: "agDateColumnFilter",
        sort: "asc",
      },
      {
        headerName: "Expected Ship Date",
        field: "ExpectedShipDate",
        valueGetter: (params) =>
          new Date(params.data.ExpectedShipDate).setHours(0, 0, 0, 0),
        cellRenderer: "dateCell",
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Bad Address",
        field: "BadAddressStatus",
        valueGetter: (params) => {
          let badAddressStatus = params.data.BadAddressStatus;
          if (
            params.data.ReviewReason.includes(
              "SHIP TO address verification failed!"
            ) &&
            (badAddressStatus === null ||
              badAddressStatus.toLowerCase() !== "complete")
          ) {
            return "True";
          }
          return "";
        },
      },
      {
        headerName: "Status",
        field: "Status",
      },
      {
        headerName: "Ship State",
        field: "ShipState",
      },
      {
        headerName: "# Plants",
        field: "CountPlants",
        filter: "agNumberColumnFilter",
        valueGetter: (params) => Number(params.data.CountPlants),
      },
      {
        headerName: "# Oversized",
        field: "CountOversized",
        filter: "agNumberColumnFilter",
        valueGetter: (params) => Number(params.data.CountOversized),
      },
      {
        headerName: "Grand Total",
        field: "FinalGrandTotal",
        valueGetter: (params) => Number(params.data.FinalGrandTotal),
        cellRenderer: (params) => moneyFormatter.format(params.value),
        filter: "agNumberColumnFilter",
        filterParams: {
          suppressAndOrCondition: true,
        },
      },
    ];
    this.frameworkComponents = {
      dateCell: date,
    };
    this.loadReport();
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
  created() {
    ModalBus.$on("freight:review:refresh", this.loadReport);
  },
  methods: {
    loadReport() {
      this.isLoading = true;
      this.showActionsMenu = false;
      this.gridOptions?.api?.showLoadingOverlay();

      Reports.getFreightReport()
        .then((response) => (this.rowData = response.data.body))
        .finally(() => (this.isLoading = false));
    },
    toggleActionsMenu() {
      this.showActionsMenu = !this.showActionsMenu;
    },
    itemSelectionChanged() {
      this.selectedRows = this.gridOptions.api.getSelectedRows();
    },
    confirmRejectFreight() {
      ModalBus.$emit("open", {
        component: FreightRejectConfirm,
        closeOnOutsideClick: false,
        props: {
          orders: this.selectedRows,
        },
      });
    },
    exportCSV() {
      if (!this.gridHasData) return;
      function formattingFunction(params) {
        return (params.column.getColId() === "OrderDate" ||
          params.column.getColId() === "ExpectedShipDate") &&
          params.value
          ? new Date(params.value).toISOString().substr(0, 10)
          : params.value;
      }
      this.gridApi.exportDataAsCsv({
        fileName: this.$options.name + "-" + dateFormatter.format(new Date()),
        processCellCallback: formattingFunction,
      });
    },
    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    onGridReady() {},
  },
};
</script>
<style lang="scss">
@import "../../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
@import "../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";

.ag-header-cell-label .ag-header-cell-text {
  text-align: center;
  white-space: normal !important;
}
</style>
